import { useState, type FormEvent } from "react";
import { Link } from "wouter";
import { Bookmark, CalendarDays, MailCheck, MailWarning, Pencil, Star, Users } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gqlRequest } from "../lib/gql";
import { MY_PROFILE, UPDATE_AVATAR, UPDATE_PROFILE, type MyProfileResult, type UpdateAvatarResult } from "../lib/queries";
import { AuthError, changePassword, sendVerificationEmail, useAuth, useMe } from "../lib/auth";
import { type Activity } from "../lib/format";
import { useT } from "../lib/i18n";
import { uploadPhoto } from "../lib/photos";
import { LoginGate } from "../components/LoginGate";
import { OutingCard } from "../components/OutingCard";
import { Avatar } from "../components/Avatar";
import { ProfileMap } from "../map/ProfileMap";
import { ActivityList, EmptyState, ErrorBox, Field, Spinner, TextInput } from "../components/ui";
import { Button } from "@/components/ui/button";

export function ProfilePage() {
  return (
    <LoginGate action="gateAction.seeProfile" next="/profile">
      <Profile />
    </LoginGate>
  );
}

function Profile() {
  const { personId, logout } = useAuth();
  const { t } = useT();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["profile", personId],
    queryFn: () => gqlRequest<MyProfileResult>(MY_PROFILE, { id: personId }),
  });

  const rename = useMutation({
    mutationFn: (displayName: string) => gqlRequest(UPDATE_PROFILE, { id: personId, displayName }),
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ["profile", personId] });
      queryClient.invalidateQueries({ queryKey: ["navPerson", personId] });
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  const person = data?.person;
  if (!person) return <EmptyState>{t("profile.loadError")}</EmptyState>;

  const memberships = data?.outingPeople.nodes ?? [];
  const owned = memberships.filter((m) => m.role === "OWNER");
  const joined = memberships.filter((m) => m.role === "PARTICIPANT");

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <div className="mb-8 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
          <div className="flex min-w-0 items-start gap-4">
            <AvatarEditor person={person} />
            <div className="min-w-0 pt-1">
              {editing ? (
                <form
                  className="flex flex-wrap items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (name.trim()) rename.mutate(name.trim());
                  }}
                >
                  <TextInput autoFocus value={name} onChange={(e) => setName(e.target.value)} className="w-56" />
                  <Button type="submit" disabled={rename.isPending}>
                    {t("common.save")}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                    {t("common.cancel")}
                  </Button>
                </form>
              ) : (
                <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-foreground">
                  {person.displayName || t("common.unnamed")}
                  <button
                    type="button"
                    title={t("profile.editName")}
                    onClick={() => {
                      setName(person.displayName ?? "");
                      setEditing(true);
                    }}
                    className="text-muted-foreground transition-colors hover:text-primary"
                    aria-label={t("profile.editName")}
                  >
                    <Pencil aria-hidden className="size-4" />
                  </button>
                </h1>
              )}
              {person.activities && person.activities.length > 0 && (
                <p className="mt-1 text-sm text-muted-foreground">
                  <ActivityList activities={person.activities as Activity[]} />
                </p>
              )}
              <EmailVerifiedLine />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline">
              <Link href={`/person/${personId}`}>
                <Users aria-hidden className="size-4" />
                {t("profile.publicProfile")}
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-primary/30 bg-primary/5 font-semibold text-primary hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
            >
              <Link href="/ticklist">
                <Star aria-hidden className="size-4" fill="currentColor" />
                {t("nav.ticklist")}
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-primary/30 bg-primary/5 font-semibold text-primary hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
            >
              <Link href="/saved">
                <Bookmark aria-hidden className="size-4" fill="currentColor" />
                {t("nav.saved")}
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-primary/30 bg-primary/5 font-semibold text-primary hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
            >
              <Link href="/calendar">
                <CalendarDays aria-hidden className="size-4" />
                {t("nav.calendar")}
              </Link>
            </Button>
            <Button type="button" variant="outline" onClick={logout}>
              {t("profile.logout")}
            </Button>
          </div>
        </div>
        <EmailVerifyBanner />
        {rename.error != null && <ErrorBox error={rename.error} />}
      </div>

      <ProfileMap personId={person.id} className="mb-8" />

      <h2 className="mb-3 text-lg font-semibold tracking-tight text-foreground">{t("profile.myOutings")} ({owned.length})</h2>
      {owned.length === 0 && <EmptyState>{t("profile.noOutings")}</EmptyState>}
      <div className="space-y-3">
        {owned.map((m) => (
          <OutingCard key={m.outing.id} outing={m.outing} />
        ))}
      </div>

      {joined.length > 0 && (
        <>
          <h2 className="mb-3 mt-8 text-lg font-semibold tracking-tight text-foreground">
            {t("profile.joinedAs")} ({joined.length})
          </h2>
          <div className="space-y-3">
            {joined.map((m) => (
              <OutingCard key={m.outing.id} outing={m.outing} />
            ))}
          </div>
        </>
      )}

      <h2 className="mb-3 mt-8 text-lg font-semibold tracking-tight text-foreground">{t("profile.changePassword")}</h2>
      <ChangePasswordCard />
    </div>
  );
}

/**
 * Avatar display + upload/remove (F22). Upload reuses the photod flow outing
 * photos use (uploadPhoto → storage_key → updatePerson.avatarKey); removing
 * just nulls the column. Replaced/removed files orphan on disk, which is fine
 * — photod keys are random and unlisted.
 */
function AvatarEditor({ person }: { person: { id: string; displayName: string | null; avatarKey: string | null } }) {
  const { t } = useT();
  const queryClient = useQueryClient();

  const setAvatar = useMutation({
    mutationFn: async (file: File | null) => {
      const avatarKey = file ? await uploadPhoto(file) : null;
      return gqlRequest<UpdateAvatarResult>(UPDATE_AVATAR, { id: person.id, avatarKey });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", person.id] });
      queryClient.invalidateQueries({ queryKey: ["navPerson", person.id] });
    },
  });

  return (
    <div className="flex flex-col items-center gap-1">
      <Avatar person={person} size="xl" />
      <label className="cursor-pointer text-xs font-semibold text-primary transition-colors hover:underline">
        {setAvatar.isPending ? t("common.saving") : person.avatarKey ? t("avatar.change") : t("avatar.upload")}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          disabled={setAvatar.isPending}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) setAvatar.mutate(file);
          }}
        />
      </label>
      {person.avatarKey != null && !setAvatar.isPending && (
        <button
          type="button"
          onClick={() => setAvatar.mutate(null)}
          className="text-xs text-muted-foreground transition-colors hover:text-destructive"
        >
          {t("common.remove")}
        </button>
      )}
      {setAvatar.error != null && (
        <p className="max-w-32 text-center text-xs text-destructive">
          {setAvatar.error instanceof Error ? setAvatar.error.message : t("avatar.uploadFailed")}
        </p>
      )}
    </div>
  );
}

/**
 * Email + verification state, from GET /auth/me rather than anything typed at
 * login — authd is the source of truth, so this survives refresh-token-only
 * sessions. Verified renders as a quiet line under the name; unverified as a
 * full-width banner row (EmailVerifyBanner) so it doesn't cramp the header.
 */
function EmailVerifiedLine() {
  const { t } = useT();
  const { data: me } = useMe();

  if (!me?.email_verified) return null;
  return (
    <p className="mt-1.5 flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
      <MailCheck aria-hidden className="size-3.5 shrink-0" />
      {me.email} · {t("email.verified")}
    </p>
  );
}

function EmailVerifyBanner() {
  const { t } = useT();
  const { data: me } = useMe();
  const [state, setState] = useState<"idle" | "busy" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  if (!me || me.email_verified) return null;

  async function onSend() {
    setState("busy");
    setError(null);
    try {
      await sendVerificationEmail();
      setState("sent");
    } catch (err) {
      setState("idle");
      setError(err instanceof Error ? err.message : t("email.sendFailed"));
    }
  }

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-800 dark:text-amber-300 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-x-1.5 font-medium">
          <MailWarning aria-hidden className="size-4 shrink-0" />
          {t("email.notVerified")}
          <span className="font-normal text-amber-700 dark:text-amber-400">· {me.email}</span>
        </p>
        {error && <p className="mt-1 text-destructive">{error}</p>}
      </div>
      {state === "sent" ? (
        <p className="shrink-0 font-medium">{t("email.sentNote")}</p>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onSend}
          disabled={state === "busy"}
          className="shrink-0 self-start border-amber-300 bg-transparent text-amber-900 hover:bg-amber-100 hover:text-amber-950 dark:border-amber-800 dark:text-amber-200 dark:hover:bg-amber-900/40 dark:hover:text-amber-100 sm:self-auto"
        >
          {state === "busy" ? t("email.sending") : t("email.send")}
        </Button>
      )}
    </div>
  );
}

function ChangePasswordCard() {
  const { t } = useT();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setDone(false);
    if (next.length < 8) {
      setError(t("pw.tooShort"));
      return;
    }
    if (next !== confirm) {
      setError(t("pw.mismatch"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
      setCurrent("");
      setNext("");
      setConfirm("");
      setDone(true);
    } catch (err) {
      if (err instanceof AuthError && err.status === 401) setError(t("pw.wrongCurrent"));
      else if (err instanceof AuthError && err.status === 422) setError(t("pw.tooShort"));
      else setError(err instanceof Error ? err.message : t("pw.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-card">
      <form onSubmit={onSubmit} className="max-w-sm space-y-4">
        <Field label={t("pw.current")}>
          <TextInput
            type="password"
            required
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <Field label={t("pw.new")}>
          <TextInput
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        <Field label={t("pw.confirm")}>
          <TextInput
            type="password"
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {done && <p className="text-sm text-primary">{t("pw.changed")}</p>}
        <Button type="submit" disabled={busy}>
          {busy ? t("pw.changing") : t("pw.submit")}
        </Button>
      </form>
    </div>
  );
}
